/**
 * Revenue Flow (formerly Pipeline)
 * 
 * 3-block structure:
 * - IN PLAY: Qualified, Proposal, Negotiation (ordered by risk/value/inactivity)
 * - WON: Total revenue, recent wins, avg closing time
 * - LOST: Lost value, reason, micro-insight
 * 
 * Design aligned with Today page (dark premium, same spacing/glow/borders)
 * No drag & drop, no complex filters, no configurations
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDeals, Deal } from '@/hooks/useDeals';
import { useLeads } from '@/hooks/useLeads';
import { useUnifiedMetrics, formatCurrency } from '@/hooks/useUnifiedMetrics';
import { QuickMarkWonModal } from '@/components/deals/QuickMarkWonModal';
import { QuickMarkLostModal } from '@/components/deals/QuickMarkLostModal';
import { ReopenDealModal } from '@/components/deals/ReopenDealModal';
import { LoadingState } from '@/components/LoadingState';
import { EmptyState } from '@/components/EmptyState';
import { InPlayBlock } from '@/components/pipeline/InPlayBlock';
import { WonBlock } from '@/components/pipeline/WonBlock';
import { LostBlock } from '@/components/pipeline/LostBlock';
import { SendToPhoneModal } from '@/components/today/SendToPhoneModal';
import { ScheduleFollowUpModal } from '@/components/today/ScheduleFollowUpModal';
import {
  DollarSign,
  Target,
  Trophy,
  AlertTriangle,
  ArrowRight,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { buildTelLink, buildWhatsAppLink } from '@/core/decision-engine/eliteEngine';
import { Lead } from '@/hooks/useLeads';

export default function Pipeline() {
  const { data: deals, isLoading: dealsLoading } = useDeals();
  const { data: leads } = useLeads();
  const { metrics } = useUnifiedMetrics();
  const navigate = useNavigate();
  
  const [markWonDeal, setMarkWonDeal] = useState<Deal | null>(null);
  const [markLostDeal, setMarkLostDeal] = useState<Deal | null>(null);
  const [reopenDeal, setReopenDeal] = useState<Deal | null>(null);
  const [sendToPhoneModalOpen, setSendToPhoneModalOpen] = useState(false);
  const [sendToPhoneAction, setSendToPhoneAction] = useState<{ lead: Lead; dealValue?: number; actionType: 'call' | 'whatsapp' } | null>(null);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleLeadId, setScheduleLeadId] = useState<string | null>(null);
  const [scheduleLeadName, setScheduleLeadName] = useState<string>('');

  // Handle call
  const handleCall = (deal: Deal, lead?: Lead) => {
    const dealLead = lead || leads?.find(l => l.id === deal.lead_id);
    if (!dealLead?.phone) return;
    
    const telLink = buildTelLink(dealLead.phone);
    if (telLink) {
      window.open(telLink, '_blank');
    }
  };

  // Handle WhatsApp
  const handleWhatsApp = (deal: Deal, lead?: Lead) => {
    const dealLead = lead || leads?.find(l => l.id === deal.lead_id);
    if (!dealLead?.phone) return;
    
    const whatsappLink = buildWhatsAppLink(dealLead.phone);
    if (whatsappLink) {
      window.open(whatsappLink, '_blank');
    }
  };

  // Handle Send to Phone
  const handleSendToPhone = (deal: Deal, lead?: Lead, actionType: 'call' | 'whatsapp') => {
    const dealLead = lead || leads?.find(l => l.id === deal.lead_id);
    if (!dealLead) return;
    
    setSendToPhoneAction({
      lead: dealLead,
      dealValue: deal.value,
      actionType,
    });
    setSendToPhoneModalOpen(true);
  };

  // Handle card click - navigate to Today if at risk, otherwise to lead detail
  const handleCardClick = (deal: Deal) => {
    const lead = leads?.find(l => l.id === deal.lead_id);
    if (lead) {
      navigate(`/app/leads?id=${lead.id}`);
    }
  };

  // Handle Mark Won
  const handleMarkWon = (deal: Deal) => {
    setMarkWonDeal(deal);
  };

  // Handle Mark Lost
  const handleMarkLost = (deal: Deal) => {
    setMarkLostDeal(deal);
  };

  // Handle Reschedule
  const handleReschedule = (deal: Deal, lead?: Lead) => {
    const dealLead = lead || leads?.find(l => l.id === deal.lead_id);
    if (dealLead) {
      setScheduleLeadId(dealLead.id);
      setScheduleLeadName(dealLead.name);
      setScheduleModalOpen(true);
    }
  };

  if (dealsLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <LoadingState message="Loading revenue flow..." />
      </div>
    );
  }

  if (!deals || deals.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <EmptyState
          icon={DollarSign}
          title="No deals yet"
          description="Create deals from leads to track them through your revenue flow."
        />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Revenue Flow</h1>
          <p className="text-muted-foreground text-sm">
            See what's moving, what's won, and what's lost
          </p>
        </div>
        
        {/* Quick KPIs */}
        <div className="flex items-center gap-4">
          <Link to="/app/today" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors">
            <AlertTriangle className="w-4 h-4" />
            <span>Go to Today</span>
            <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>

      {/* Summary KPIs */}
      {deals && deals.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Link to="/app/dashboard" className="block h-full">
            <div className="rounded-lg border border-border/40 bg-card p-4 hover:border-primary/30 hover:shadow-sm transition-all cursor-pointer">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                  <DollarSign className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <p className="text-lg font-bold tabular-nums">
                    {formatCurrency(metrics?.pipelineValue || 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">Pipeline Value</p>
                </div>
              </div>
            </div>
          </Link>

          <Link to="/app/dashboard" className="block h-full">
            <div className="rounded-lg border border-border/40 bg-card p-4 hover:border-primary/30 hover:shadow-sm transition-all cursor-pointer">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-success/10 flex items-center justify-center">
                  <Target className="w-4 h-4 text-success" />
                </div>
                <div>
                  <p className="text-lg font-bold tabular-nums">
                    {formatCurrency(metrics?.weightedForecast || 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">Forecast</p>
                </div>
              </div>
            </div>
          </Link>

          <div className="rounded-lg border border-border/40 bg-card p-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-success/10 flex items-center justify-center">
                <Trophy className="w-4 h-4 text-success" />
              </div>
              <div>
                <p className="text-lg font-bold tabular-nums">
                  {(metrics?.winRate || 0).toFixed(0)}%
                </p>
                <p className="text-xs text-muted-foreground">
                  Win Rate ({metrics?.wonCount || 0}W / {metrics?.lostCount || 0}L)
                </p>
              </div>
            </div>
          </div>

          <Link to="/app/today" className="block h-full">
            <div className="rounded-lg border border-border/40 bg-card p-4 hover:border-primary/30 hover:shadow-sm transition-all cursor-pointer">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-destructive/10 flex items-center justify-center">
                  <AlertTriangle className="w-4 h-4 text-destructive" />
                </div>
                <div>
                  <p className="text-lg font-bold tabular-nums">{metrics?.needAction || 0}</p>
                  <p className="text-xs text-muted-foreground">Need Action</p>
                </div>
              </div>
            </div>
          </Link>
        </div>
      )}

      {/* 3 Blocks */}
      <div className="space-y-12">
        {/* IN PLAY */}
        <InPlayBlock
          deals={deals}
          leads={leads || []}
          onCall={handleCall}
          onWhatsApp={handleWhatsApp}
          onSendToPhone={handleSendToPhone}
          onCardClick={handleCardClick}
          onMarkWon={handleMarkWon}
          onMarkLost={handleMarkLost}
          onReschedule={handleReschedule}
        />

        {/* WON */}
        <WonBlock deals={deals} leads={leads || []} />

        {/* LOST */}
        <LostBlock deals={deals} leads={leads || []} />
      </div>

      {/* Modals */}
      {markWonDeal && (
        <QuickMarkWonModal 
          deal={markWonDeal} 
          open={!!markWonDeal} 
          onOpenChange={(open) => !open && setMarkWonDeal(null)} 
        />
      )}

      {markLostDeal && (
        <QuickMarkLostModal 
          deal={markLostDeal} 
          open={!!markLostDeal} 
          onOpenChange={(open) => !open && setMarkLostDeal(null)} 
        />
      )}

      {reopenDeal && (
        <ReopenDealModal 
          deal={reopenDeal} 
          open={!!reopenDeal} 
          onOpenChange={(open) => !open && setReopenDeal(null)} 
        />
      )}

      {sendToPhoneAction && (
        <SendToPhoneModal
          open={sendToPhoneModalOpen}
          onOpenChange={setSendToPhoneModalOpen}
          lead={sendToPhoneAction.lead}
          dealValue={sendToPhoneAction.dealValue}
          actionType={sendToPhoneAction.actionType}
        />
      )}

      {scheduleLeadId && (
        <ScheduleFollowUpModal
          open={scheduleModalOpen}
          onOpenChange={(open) => {
            setScheduleModalOpen(open);
            if (!open) {
              setScheduleLeadId(null);
              setScheduleLeadName('');
            }
          }}
          leadId={scheduleLeadId}
          leadName={scheduleLeadName}
        />
      )}
    </div>
  );
}
